import { useMemo } from "react";

import {
  MilestoneProgressSnapshot,
  MilestoneProgressSnapshotSchema,
  ProgressSnapshot,
  QuantitativeProgressSnapshot,
  QuantitativeProgressSnapshotSchema,
} from "@/domain/progress-snapshot/models/progress-snapshots-model";
import { useQuery } from "@apollo/client";

import { getProgressSnapshotQueryDocument } from "./progress-snapshot.gql";

export interface UseGetProgressSnapshotProps {
  id: string;
  enabled?: boolean;
}

export const useGetProgressSnapshot = ({ id, enabled = true }: UseGetProgressSnapshotProps) => {
  const queryResult = useQuery(getProgressSnapshotQueryDocument, {
    variables: { input: { id: id } },
    skip: !enabled,
  });

  const progressSnapshot: ProgressSnapshot | null = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }
    return queryResult.data.progressSnapshot;
  }, [queryResult.data]);

  const quantitativeProgressSnapshot: QuantitativeProgressSnapshot | null = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }
    const parsedQuantitativeProgressSnapshot = QuantitativeProgressSnapshotSchema.safeParse(
      queryResult.data.progressSnapshot,
    );
    if (parsedQuantitativeProgressSnapshot.success) {
      return parsedQuantitativeProgressSnapshot.data;
    }

    return null;
  }, [queryResult.data]);

  const milestoneProgressSnapshot: MilestoneProgressSnapshot | null = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const parsedMilestoneProgressSnapshot = MilestoneProgressSnapshotSchema.safeParse(
      queryResult.data.progressSnapshot,
    );
    if (parsedMilestoneProgressSnapshot.success) {
      return parsedMilestoneProgressSnapshot.data;
    }

    return null;
  }, [queryResult.data]);

  return {
    queryResult,
    progressSnapshot,
    quantitativeProgressSnapshot,
    milestoneProgressSnapshot,
    loading: queryResult.loading,
    error: queryResult.error,
  };
};
